import React from 'react';
import Dropzone from 'react-dropzone';
import { toasty } from '../common/forms/FormElements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faTimes,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import FileLink from '../common/forms/FileLink';
import { Button, Row, Col, Input } from 'reactstrap';
import cls from 'classnames';
import { ApiRoutes } from '../ApiRoutes';
import { util } from '../Util';

export class HeavyBidUploadButton extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            rejections: [],
            uploads: [],
            uploading: false,
            vistaJobNumber: null,
            vistaJobNumberValidated: false,
            isChangeFile: false,
        };
    }

    componentDidMount() {}

    conditionallySetFileName = (file) => {
        if (!file || !file.name) {
            return;
        }

        const fileName = file.name;

        const splitOnSpaces = fileName.split(' ');

        if (splitOnSpaces && splitOnSpaces.length > 0) {
            const vistaJobNumber = splitOnSpaces[0]
                .replace('.xml', '')
                .replace('X', '');

            toasty.info(
                `Job number automatically set to ${vistaJobNumber}. Please double-check and correct if needed.`
            );

            this.setState({ vistaJobNumber }, () =>
                this.validateVistaJobNumber()
            );
        }
    };

    onDrop = (acceptedFiles, fileRejections, event) => {
        const { uploads, uploading } = this.state;

        if (uploading) {
            return false;
        }

        // Upsert, newest wins.
        for (const af of acceptedFiles) {
            if (!uploads.filter((u) => af.path === u.path).length) {
                uploads.push({ file: af, progress: 0.0 });

                this.conditionallySetFileName(af);
            } else {
                uploads[uploads.find((x) => x.file.path === af.path)] = af;
            }
        }

        this.setState({
            uploads,
            rejections: fileRejections ?? [],
            isChangeFile: false,
        });
    };

    onProgressUpdated = (e, index) => {
        if (!e.lengthComputable) {
            console.log(
                'Unable to compute progress information since the total size is unknown'
            );
            return;
        }

        const { uploads } = this.state;
        const percentage = Math.round((e.loaded / e.total) * 100);
        uploads[index].progress = percentage;
        this.setState({ uploads });
    };

    onUploadComplete = async (xhr) => {
        const { uploads } = this.state;

        if (uploads.filter((x) => x.progress < 100.0).length) {
            return;
        }

        if (xhr.status > 200) {
            toasty.error('Error uploading file.');
        } else {
            toasty.success('HeavyBid import complete.');
        }

        await this.setState(
            {
                uploading: false,
                uploads: [],
                vistaJobNumber: null,
                vistaJobNumberValidated: false,
                isChangeFile: false,
            },
            () => {
                const { onHeavyBidUploadComplete } = this.props;

                if (onHeavyBidUploadComplete) {
                    onHeavyBidUploadComplete();
                }
            }
        );
    };

    dismissError = (file) => {
        const { rejections } = this.state;
        rejections.splice(
            rejections.findIndex((x) => x.path === file.path),
            1
        );
        this.setState({ rejections });
    };

    remove = (x) => {
        const { uploads } = this.state;
        uploads.splice(
            uploads.findIndex((x) => x.path === x.file.path),
            1
        );
        this.setState({ uploads });
    };

    upload = async () => {
        const { vistaJobNumber, uploads, isChangeFile } = this.state;

        this.setState({ uploading: true }, () => {
            const files = uploads;

            for (const x of files) {
                const index = files.indexOf(x);
                const formData = new FormData();
                formData.append('file', x.file, x.file.path);
                formData.append('VistaJobNumber', vistaJobNumber);
                formData.append('isChangeFile', isChangeFile);

                const xhr = new XMLHttpRequest();
                xhr.open('POST', ApiRoutes.heavyBidImport.import(), true);

                xhr.upload.onprogress = (e) => this.onProgressUpdated(e, index);

                xhr.onload = () => {
                    this.onUploadComplete(xhr);
                };

                xhr.send(formData);

                xhr.onerror = (e) => {
                    toasty.error(
                        'Error uploading. The dev console may have more information.'
                    );

                    console.error({ e });
                };
            }
        });
    };

    validator = (file) => {
        const maxNameLength = 150;

        return file && file.name && file.name.length > maxNameLength
            ? {
                  code: 'name-too-large',
                  message: `Name is larger than ${maxNameLength} characters`,
              }
            : null;
    };

    validateVistaJobNumber = async () => {
        const { vistaJobNumber } = this.state;

        if (
            !vistaJobNumber ||
            !vistaJobNumber.includes('-') ||
            vistaJobNumber.length < 8
        ) {
            return;
        }

        const response = await util.fetch.get(
            ApiRoutes.contract.validateJobNumber(vistaJobNumber)
        );

        if (!response) {
            toasty.error('No server response.');
            return;
        }

        if (response.isKnownJobNumber) {
            this.setState({ vistaJobNumberValidated: true });
            return;
        }

        this.setState({ vistaJobNumberValidated: false });

        toasty.error(
            'Unknown Job Number',
            <div>
                <div>
                    Sorry, we couldn't find a job with that Vista job number in
                    our database.
                </div>
                <div className="mt-2">
                    Please double-check its value. If it is correct, please try
                    running a Vista import and then trying again.
                </div>
            </div>
        );
    };

    onVistaJobNumberChange = (e) => {
        if (!e || !e.target) {
            return;
        }

        const vistaJobNumber = e.target.value;

        this.setState({ vistaJobNumber }, () => {
            this.validateVistaJobNumber();
        });
    };

    onIsChangeFileChange = (e) => {
        if (!e || !e.target) {
            return;
        }

        const isChangeFile = e.target.checked;

        this.setState({ isChangeFile });
    };

    render() {
        const {
            uploads,
            rejections,
            uploading,
            vistaJobNumber,
            vistaJobNumberValidated,
            isChangeFile,
        } = this.state;

        return (
            <Dropzone
                onDrop={this.onDrop}
                inputContent={(files, extra) =>
                    extra.reject
                        ? 'File type not permitted.'
                        : 'Drag and drop your files here, or click to select'
                }
                validator={this.validator}
                maxSize={25000000}
            >
                {({ getRootProps, getInputProps }) => (
                    <>
                        {!!(uploads ?? []).length && (
                            <div className="file-uploads-preview">
                                {uploads.map((x) => (
                                    <FileLink
                                        preview
                                        showFileSize
                                        key={x.file.path}
                                        url="#"
                                        file={x.file}
                                        remove={() => this.remove(x)}
                                        progress={x.progress}
                                    />
                                ))}
                                <Row>
                                    <Col>Vista Job Number</Col>
                                    <Col>
                                        <Input
                                            type="text"
                                            onChange={
                                                this.onVistaJobNumberChange
                                            }
                                            value={vistaJobNumber}
                                            placeholder="Expected to be in a xx-yyyyy number format."
                                            invalid={!vistaJobNumberValidated}
                                            readOnly={uploading}
                                        />
                                    </Col>
                                </Row>
                                <Row className="mt-2">
                                    <Col>Change File</Col>
                                    <Col>
                                        <Input
                                            type="checkbox"
                                            className="ml-auto mr-auto is-change-file-checkbox"
                                            onChange={this.onIsChangeFileChange}
                                            value={isChangeFile}
                                            readOnly={uploading}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        )}
                        <div className="file-upload-toolbar ">
                            {!!(uploads ?? []).length && (
                                <Button
                                    disabled={
                                        uploading || !vistaJobNumberValidated
                                    }
                                    type="button"
                                    size="sm"
                                    className={cls('file-upload-button mb-2', {
                                        disabled:
                                            uploading ||
                                            !vistaJobNumberValidated,
                                    })}
                                    color="primary"
                                    onClick={this.upload}
                                >
                                    <FontAwesomeIcon
                                        icon={faUpload}
                                        className="mr-2"
                                        size="lg"
                                    />
                                    {uploading
                                        ? 'Uploading, please wait...'
                                        : 'Upload'}
                                </Button>
                            )}
                        </div>
                        {!!(rejections ?? []).length && (
                            <div className="file-uploads-preview mt-2">
                                {rejections.map(({ file, errors }) => (
                                    <div
                                        className="file-upload-error p-2 mb-1"
                                        key={file.path}
                                    >
                                        <div className="d-flex flex-row justify-content-between">
                                            <div className="file-upload-error-name">
                                                <FontAwesomeIcon
                                                    icon={faExclamationTriangle}
                                                    size="lg"
                                                    className="mr-2"
                                                />
                                                {file.path}
                                            </div>
                                            <div className="flex-fill d-flex flex-row justify-content-end">
                                                <FontAwesomeIcon
                                                    icon={faTimes}
                                                    className="cursor-pointer"
                                                    onClick={() =>
                                                        this.dismissError(file)
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <ul>
                                            {errors.map((e) => (
                                                <li key={e.code}>
                                                    <small>{e.message}</small>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                        <section
                            className="file-upload-section"
                            hidden={uploading || uploads.length}
                        >
                            <div
                                {...getRootProps({ className: 'dropzone' })}
                                className="file-upload-section-inner"
                            >
                                <input {...getInputProps()} />
                                <span className="border-bottom mb-2">
                                    Drag and drop your HeavyBid XML files here,
                                    or click this to open up a file browser.
                                </span>
                                <small>
                                    Supported file types:
                                    <span className="ml-2 text-success">
                                        XML
                                    </span>
                                </small>
                                <small>
                                    File names must be less than 150 characters.
                                </small>
                            </div>
                        </section>
                    </>
                )}
            </Dropzone>
        );
    }
}
